/**
 * 모든 화면 위에 고정으로 붙는 상단 막대. 거의 모든 페이지에서 공통으로 렌더링된다.
 *
 * 로그인 여부(`type`)에 따라 오른쪽 영역이 갈린다.
 * - 로그인 전: "해봄 체험하기" 버튼 + 로그인 링크
 * - 로그인 후: 프로필 아바타(클릭 시 `UserMenu` 드롭다운). 선생님 계정이면 아바타 왼쪽에
 *   이메일을 함께 보여준다(선생님은 화면만 봐서는 어떤 계정으로 들어왔는지 알 수 없기 때문).
 *
 * `hidden` prop 이 true 면 로고만 남기고 로그인 영역을 비운다(체험하기 화면 등에서 사용).
 */
import React, { useState, useRef, useEffect } from "react";
import Logo from "../icons/Logo";
import UserMenu from "../home/UserMenu";
import ExhibitionAgeSelectModal from "../modal/ExhibitionAgeSelectModal";
import ExhibitionPosterModal from "../modal/ExhibitionPosterModal";
import useNewPlayFlow from "@/hooks/useNewPlayFlow";
import { useRouter } from "next/router";
import { useRecoilState } from "recoil";
import { userState } from "@/recoil/atom";
import fetchUserInfo from "pages/api/home/fetchUserInfo";

interface HeaderProps {
  /** true 면 로그인 영역(체험하기·로그인·프로필)을 비운다. */
  hidden?: boolean;
}

const Header: React.FC<HeaderProps> = ({ hidden = false }) => {
  const router = useRouter();
  const [userInfo, setUserInfo] = useRecoilState(userState);
  const [userId, setUserId] = useState("");
  const [type, setType] = useState(1); // 1이면 로그인 전, 2이면 로그인 후
  // 선생님 계정은 어떤 계정으로 로그인 중인지 화면에서 확인할 방법이 없어
  // 프로필 아바타 왼쪽에 계정 이메일을 노출한다. role은 로그인 시 sessionStorage 저장.
  const [isTeacherAccount, setIsTeacherAccount] = useState(false);

  // 스크롤 16px 이상에서 그림자/반투명 전환
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 16);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // userId가 없는 경우에, userId와 type 설정 (사실상, 처음 컴포넌트 렌더링 시에)
  useEffect(() => {
    if (!userId && typeof window !== "undefined") {
      const storedUserId = sessionStorage.getItem("userId");
      setUserId(storedUserId ?? "");
      setType(storedUserId === null ? 1 : 2);
      setIsTeacherAccount(sessionStorage.getItem("role") === "TEACHER");
    }
  }, [userId]);

  // Header에서 recoil 변수 업데이트 함 (토큰 리뉴얼 시에 recoil은 업데이트 되지 않기 때문)
  // Header가 모든 페이지에 공통으로 들어가기 때문에 여기서 실행함
  useEffect(() => {
    const getUserData = async () => {
      if (!hidden && userId && userInfo.name === "") {
        const result = await fetchUserInfo(userId);
        if (result) setUserInfo(result.userInfo);
      }
    };
    getUserData();
  }, [userId, userInfo.name]);

  const [isMenuClick, setIsMenuClick] = useState(false);
  const [isAgeModalOpen, setIsAgeModalOpen] = useState(false);
  // `?new=1` 이면 체험하기 개편판(포스터형) 모달, 아니면 기존 참여자 유형 모달.
  const isNewPlayFlow = useNewPlayFlow();
  const menuRef = useRef<HTMLDivElement>(null); // 메뉴 영역을 참조할 ref

  // 외부 영역 클릭 시, 메뉴 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuClick(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMenuClick = () => {
    setIsMenuClick(!isMenuClick);
  };

  // 로고 클릭 시 역할별 홈으로 이동. 선생님(role === "TEACHER")은 /teacher,
  // 학생 또는 role 미저장(legacy)은 기존대로 "/". 학생 화면으로 새면 안 됨.
  // 클릭 핸들러라 prerender 시점에 실행되지 않음 → sessionStorage 직접 접근 안전(typeof window 가드만 유지).
  const handleLogoClick = () => {
    const isTeacher =
      typeof window !== "undefined" &&
      sessionStorage.getItem("role") === "TEACHER";
    router.push(isTeacher ? "/teacher" : "/");
  };

  // 선생님 화면(/teacher/**)에서는 로고 옆에 "선생님" 라벨을 붙여 역할을 구분한다.
  // router.pathname 기반이라 prerender(static export) 시점에도 안전.
  const isTeacherPage = router.pathname.startsWith("/teacher");

  return (
    <header
      className={`px-10 py-4 border-b border-line w-full sticky top-0 z-40 transition-shadow duration-150 ${
        isScrolled ? "bg-white/95 backdrop-blur shadow-sm" : "bg-white"
      }`}
    >
      <div className="flex justify-between items-center">
        <div className="cursor-pointer flex gap-x-[50px] items-center">
          <div
            onClick={handleLogoClick}
            className="flex items-center gap-x-[6px]"
          >
            <Logo />
            {isTeacherPage && (
              <span className="inline-flex items-center rounded-md bg-accent px-2 py-1 text-13 font-semibold leading-none text-heading">
                선생님용
              </span>
            )}
          </div>
        </div>

        {hidden ? (
          <></>
        ) : type === 1 ? (
          <div className="flex gap-x-[30px] items-center">
            <button
              type="button"
              onClick={() => setIsAgeModalOpen(true)}
              className="text-15 text-primary font-bold cursor-pointer hover:text-primary-ink transition-colors"
            >
              해봄 체험하기
            </button>
            <div
              onClick={() => router.push("/login")}
              className="text-15 text-body font-normal cursor-pointer"
            >
              로그인
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-x-[10px]">
            {isTeacherAccount && userInfo?.email && (
              <span className="text-13 text-muted font-normal">
                {userInfo.email}
              </span>
            )}
            <button
              type="button"
              onClick={handleMenuClick}
              aria-haspopup="menu"
              aria-label="내 계정 메뉴 열기"
              className="bg-primary-muted w-[30px] h-[30px] flex items-center justify-center rounded-full cursor-pointer overflow-hidden"
            >
            {/*
              아바타 통일:
              사진 우선 → 없으면 이름 뒤 2글자. 기존 오버플로 버그(50px img + mt-4)를
              w-full h-full object-cover 로 정리.
            */}
            {userInfo?.profileImageUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={userInfo.profileImageUrl}
                alt={`${userInfo?.name ?? "회원"} 프로필 사진`}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <span className="text-12 text-primary font-normal" aria-hidden="true">
                {(userInfo?.name || "회원").slice(-2)}
              </span>
            )}
            </button>
          </div>
        )}
      </div>
      {isMenuClick && (
        <div ref={menuRef}>
          <UserMenu setIsMenuClick={setIsMenuClick} />
        </div>
      )}
      {isNewPlayFlow ? (
        <ExhibitionPosterModal isOpen={isAgeModalOpen} onClose={() => setIsAgeModalOpen(false)} />
      ) : (
        <ExhibitionAgeSelectModal isOpen={isAgeModalOpen} onClose={() => setIsAgeModalOpen(false)} />
      )}
    </header>
  );
};

export default Header;
